import sleep from '@flipster/utils/sleep';
import { datadog } from '@flipster/utils/datadog';

import AbstractSocket, { type AbstractSocketEvents } from './AbstractSocket';

const MinJitterMillis = 250;
const MaxJitterMillis = 1000;
const MaxBackoffCount = 5;
function getRetryDelay(attempt: number): number {
  const threshold = Math.pow(2, Math.min(attempt, MaxBackoffCount));
  // 1s, 2s, 4s, 8s, 16s, 16s, ...
  const millisOfExponentialBackoff = 500 * threshold;

  const maxJitterByAttempt = MaxJitterMillis * threshold;
  const minJitterByAttempt = MinJitterMillis * threshold;
  const millisOfJitter = Math.random() * (maxJitterByAttempt - minJitterByAttempt) + minJitterByAttempt;
  return millisOfExponentialBackoff + millisOfJitter;
}

function startReconnectionLoop(debugLabel: string, connect: () => Promise<void>, done: () => boolean): () => void {
  let running = true;
  (async () => {
    for (let attempt = 1; running && !done(); attempt += 1) {
      try {
        return await connect();
      } catch {
        /* ignore */
      }
      await sleep(getRetryDelay(attempt));
      if (attempt > 10) {
        // oxlint-disable-next-line no-console
        console.warn(`[${debugLabel}] - Retry socket connection ${attempt}...`);
      }
    }
  })();
  return () => {
    running = false;
  };
}

function onActive(fn: () => void): () => void {
  window.addEventListener('focus', fn);
  window.addEventListener('online', fn);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') fn();
  });
  return () => {
    window.removeEventListener('focus', fn);
    window.removeEventListener('online', fn);
    document.removeEventListener('visibilitychange', fn);
  };
}

export default abstract class AutoReconnectingSocket<
  D extends unknown,
  E extends AbstractSocketEvents = AbstractSocketEvents,
> extends AbstractSocket<D, E> {
  protected needReconnection = false;
  #stopReconnectionLoop: (() => void) | undefined;
  #offActive: (() => void) | undefined;

  #connectionTimeout: NodeJS.Timeout | null = null;

  protected constructor() {
    super();
    this.on('connected', () => {
      // FIXME: May we need to move connected timing log to `AbstractSocket`?
      // But like `IntervalDataSocket`, that extends `AbstractSocket` but also use `AbstractSocket` as instance variable
      datadog.addTiming('socket_connected');
      if (this.#connectionTimeout) {
        clearTimeout(this.#connectionTimeout);
        this.#connectionTimeout = null;
      }
      this.#stopReconnectionLoop?.();
      this.#stopReconnectionLoop = undefined;
    });
    this.on('closed', () => {
      if (!this.needReconnection) return;
      this.#connectionTimeout ??= setTimeout(() => {
        this.#notifyConnectionRecoverTimeoutListener();
      }, 5000);
      if (this.#stopReconnectionLoop) return;
      this.#stopReconnectionLoop = startReconnectionLoop(
        this.debugLabel,
        () => this.tryConnect(),
        () => this.state === 'connected' || !this.needReconnection,
      );
    });
  }

  /**
   * 이 메서드는 멱등하게 구현되어야 합니다.
   */
  protected tryConnect(): Promise<void> {
    if (this.state === 'connected') return Promise.resolve();
    if (this.state === 'connecting') {
      return new Promise<void>((resolve) => {
        this.once('connected', resolve);
      });
    }
    return Promise.reject(new Error('Not implemented'));
  }

  connect() {
    this.needReconnection = true;
    this.#offActive?.();
    // 브라우저는 비활성화된 탭에 강한 스로틀링을 걸기 때문에
    // 사용자가 오랫동안 다른 탭을 보다가 돌아오면 소켓 연결이 끊어져있을 수 있습니다.
    // 소켓 연결이 끊어지면 reconnection loop가 돌기 때문에 어차피 언젠가는 다시 재연결이 되긴 하겠지만,
    // 사용자가 다른 탭을 오랫동안 보고있다가 돌아오는 순간에는
    // exponential backoff로 인해 reconnection loop의 주기가 한참 길어진 상태일 수 있기 때문에
    // 빠른 반응을 위해 탭이 활성화된 시점에 바로 재연결을 시도합니다.
    this.#offActive = onActive(() => this.tryConnect().catch(() => {}));
    this.tryConnect().catch(() => {});
  }

  close(): void {
    this.needReconnection = false;
    this.#offActive?.();
    this.#offActive = undefined;
  }

  #notifyConnectionRecoverTimeoutListener() {
    this.emit('reconnectionLongTerm', undefined);
  }
}
